import type { LayerAPI } from "@/types/stage-types";
import { updateSketch } from "@/api";
import "./UIDedicatedLayer.scss";
import {
  createDSPlaceholder,
  type DSPlaceholderAPI,
} from "./subcomponents/DS/DS";
import {
  createCMSPlaceholder,
  type CMSPlaceholderAPI,
} from "./subcomponents/CMS/CMS";
import {
  createShowAllPages,
  type ShowAllPagesAPI,
  type ShowAllItem,
} from "./subcomponents/ShowAllPages/ShowAllPages";
import {
  createShowAllSketches,
  type ShowAllSketchesAPI,
  type ShowAllSketchItem,
} from "./subcomponents/ShowAllSketches/ShowAllSketches";
import type {
  PageSummary,
  SketchSummary,
} from "@/types/project-types";

export type UIDedicatedViewId =
  | "ds"
  | "cms"
  | "showAllPages"
  | "showAllSketches"
  | null;

export interface UIDedicatedLayerContext {
  readonly mode: string | null | undefined;
  readonly showAll: {
    readonly edit: boolean;
    readonly sketch: boolean;
  };
}

export interface UIDedicatedLayerConfig {
  readonly appStateManager?: any;
}

export interface UIDedicatedLayerAPI extends LayerAPI {
  readonly updateContext: (context: UIDedicatedLayerContext) => void;
  readonly setPages: (pages: readonly ShowAllItem[]) => void;
  readonly setSketches: (sketches: readonly ShowAllSketchItem[]) => void;
}

const computeViewId = (context: UIDedicatedLayerContext): UIDedicatedViewId => {
  const mode = context.mode ?? null;
  if (!mode) return null;

  if (mode === "ds") return "ds";
  if (mode === "cms") return "cms";
  if (mode === "edit" && context.showAll?.edit) return "showAllPages";
  if (mode === "sketch" && context.showAll?.sketch) return "showAllSketches";
  return null;
};

export const createUIDedicatedLayer = (
  container: HTMLElement,
  config: UIDedicatedLayerConfig = {}
): UIDedicatedLayerAPI => {
  let isDestroyed = false;
  let currentView: UIDedicatedViewId = null;
  let pages: ShowAllItem[] = [];
  let sketches: ShowAllSketchItem[] = [];
  let currentProjectId: string | null = null;
  let selectedSketchId: string | null = null;

  const layerRoot = document.createElement("div");
  layerRoot.className = "ui-dedicated-layer";

  const inner = document.createElement("div");
  inner.className = "ui-dedicated-layer__inner";
  layerRoot.appendChild(inner);

  container.appendChild(layerRoot);
  const appStateManager = config.appStateManager;
  const eventBus = appStateManager?.appContext?.eventBus;

  const handleSketchSelected = (item: ShowAllSketchItem): void => {
    if (!item?.id) return;
    eventBus?.emit("sketch-selected", { sketchId: item.id });
    appStateManager?.uiState?.setShowAll?.("sketch", false);
  };

  const handleSketchRenamed = async (
    item: ShowAllSketchItem,
    nextName: string
  ): Promise<string | null> => {
    const title = nextName.trim();
    if (!title || !item?.id || !currentProjectId) return null;
    const response = await updateSketch(currentProjectId, item.id, { title });
    if (!response?.ok) return null;
    const resolvedTitle = response.sketch?.title?.trim() || title;
    const nextSketches = sketches.map((sketch) =>
      sketch.id === item.id ? { ...sketch, name: resolvedTitle } : sketch
    );
    setSketches(nextSketches);

    eventBus?.emit("sketch-renamed", {
      projectId: currentProjectId,
      sketchId: item.id,
      title: resolvedTitle,
      source: "local",
    });

    return resolvedTitle;
  };

  type ActiveView =
    | { readonly id: "ds"; readonly api: DSPlaceholderAPI }
    | { readonly id: "cms"; readonly api: CMSPlaceholderAPI }
    | { readonly id: "showAllPages"; readonly api: ShowAllPagesAPI }
    | { readonly id: "showAllSketches"; readonly api: ShowAllSketchesAPI };

  let activeView: ActiveView | null = null;

  const detachActiveView = () => {
    const view = activeView;
    if (!view) return;
    try {
      view.api.setVisible(false);
      view.api.destroy();
    } catch {}
    activeView = null;
    inner.innerHTML = "";
  };

  const attachView = (nextView: Exclude<UIDedicatedViewId, null>) => {
    if (isDestroyed) return;
    const current = activeView;
    if (current && current.id === nextView) {
      if (current.id === "showAllPages") {
        current.api.setPages(pages);
      } else if (current.id === "showAllSketches") {
        current.api.setSketches(sketches);
      }
      return;
    }

    detachActiveView();

    let nextActive: ActiveView | null = null;
    switch (nextView) {
      case "ds":
        {
          const api = createDSPlaceholder();
          inner.appendChild(api.getElement());
          api.setVisible(true);
          nextActive = { id: "ds", api };
        }
        break;
      case "cms":
        {
          const api = createCMSPlaceholder();
          inner.appendChild(api.getElement());
          api.setVisible(true);
          nextActive = { id: "cms", api };
        }
        break;
      case "showAllPages":
        {
          const api = createShowAllPages();
          inner.appendChild(api.getElement());
          api.setPages(pages);
          api.setVisible(true);
          nextActive = { id: "showAllPages", api };
        }
        break;
      case "showAllSketches":
        {
          const api = createShowAllSketches({
            onSelectSketch: handleSketchSelected,
            onRenameSketch: handleSketchRenamed,
          });
          inner.appendChild(api.getElement());
          api.setSketches(sketches);
          api.setVisible(true);
          nextActive = { id: "showAllSketches", api };
        }
        break;
      default:
        return;
    }

    activeView = nextActive;
  };

  const updateViews = (nextView: UIDedicatedViewId) => {
    if (isDestroyed) return;
    currentView = nextView;

    if (!nextView) {
      detachActiveView();
      return;
    }

    attachView(nextView);
  };

  const updateContext = (context: UIDedicatedLayerContext) => {
    if (isDestroyed) return;
    const nextView = computeViewId(context);
    updateViews(nextView);
  };

  const setPages = (items: readonly ShowAllItem[]) => {
    pages = [...items];
    const view = activeView;
    if (view && view.id === "showAllPages") {
      view.api.setPages(pages);
    }
  };

  const setSketches = (items: readonly ShowAllSketchItem[]) => {
    sketches = [...items];
    const view = activeView;
    if (view && view.id === "showAllSketches") {
      view.api.setSketches(sketches);
    }
  };

  const setVisible = (visible: boolean) => {
    if (isDestroyed) return;
    layerRoot.classList.toggle("ui-dedicated-layer--visible", visible);
    // Ensure the container itself does not intercept pointer events when hidden
    // This prevents blocking interactions with layers underneath
    (container as HTMLElement).style.pointerEvents = visible ? "auto" : "none";
  };

  const destroy = () => {
    if (isDestroyed) return;
    isDestroyed = true;

    detachActiveView();
    layerRoot.remove();

    unsubscribeProjectChanged?.();
    unsubscribeProjectsAvailable?.();
    unsubscribeSketchCreated?.();
    unsubscribeSketchChanged?.();
  };

  const mapPages = (
    summaries: readonly PageSummary[] | undefined
  ): ShowAllItem[] => {
    if (!Array.isArray(summaries)) return [];
    return summaries.map((summary) => {
      const preview = Array.isArray(summary.previews)
        ? summary.previews[0] ?? null
        : null;
      return {
        id: summary.id,
        name: summary.name,
        description: summary.slug,
        preview: preview
          ? {
              url: preview.url,
              kind: preview.kind,
              width: preview.w,
              height: preview.h,
              createdAt: preview.createdAt,
            }
          : null,
      };
    });
  };

  const mapSketches = (
    summaries: readonly SketchSummary[] | undefined,
    selectedId: string | null
  ): ShowAllSketchItem[] => {
    if (!Array.isArray(summaries)) return [];
    return summaries.map((summary) => {
      const preview = Array.isArray(summary.previews)
        ? summary.previews[0] ?? null
        : null;
      return {
        id: summary.id,
        name: summary.title ?? "Untitled Sketch",
        selected: summary.id === selectedId,
        preview: preview ? { url: preview.url, kind: preview.kind } : null,
      };
    });
  };

  let unsubscribeProjectChanged: (() => void) | null = null;
  let unsubscribeProjectsAvailable: (() => void) | null = null;
  let unsubscribeSketchCreated: (() => void) | null = null;
  let unsubscribeSketchChanged: (() => void) | null = null;

  if (eventBus) {
    unsubscribeProjectChanged = eventBus.on(
      "project-changed",
      (payload: any) => {
        currentProjectId =
          typeof payload?.projectId === "string" ? payload.projectId : null;
        const pagesSource =
          (payload?.pages as PageSummary[] | undefined) ??
          (payload?.bootstrap?.pages as PageSummary[] | undefined);
        const sketchesSource =
          (payload?.sketches as SketchSummary[] | undefined) ??
          (payload?.bootstrap?.sketches as SketchSummary[] | undefined);
        selectedSketchId =
          typeof payload?.selectedSketchId === "string"
            ? payload.selectedSketchId
            : selectedSketchId;
        setPages(mapPages(pagesSource));
        setSketches(mapSketches(sketchesSource, selectedSketchId));
      }
    );

    unsubscribeProjectsAvailable = eventBus.on(
      "projects-available",
      (payload: any) => {
        if (Array.isArray(payload?.projects) && payload.projects.length) {
          // No direct data needed yet, but keep hook for future enhancements
        }
      }
    );

    unsubscribeSketchCreated = eventBus.on(
      "sketch-created",
      (payload: any) => {
        const sketch = payload?.sketch as SketchSummary | undefined;
        if (!sketch) return;
        const next = mapSketches([sketch], selectedSketchId)[0];
        if (!next) return;
        setSketches([...sketches.filter((item) => item.id !== next.id), next]);
      }
    );

    unsubscribeSketchChanged = eventBus.on(
      "sketch-changed",
      (payload: any) => {
        const nextSelected =
          typeof payload?.sketchId === "string" ? payload.sketchId : null;
        if (!nextSelected) return;
        selectedSketchId = nextSelected;
        setSketches(
          sketches.map((item) => ({
            ...item,
            selected: item.id === selectedSketchId,
          }))
        );
      }
    );

    eventBus.emit("request-projects", {});
    eventBus.emit("request-current-selection", {});
  }

  return {
    updateContext,
    setPages,
    setSketches,
    setVisible,
    destroy,
  };
};
